// src/tracks/TrackManager.js
// Owns the collection of Tracks. Handles instrument loading/binding,
// audio routing, scheduling, and the solo/mute logic.

import * as Tone from 'tone';
import { Track } from './Track.js';
import { loadInstrument, getInstrumentEntry } from '../audio/instruments.js';
import { engine } from '../audio/engine.js';
import { clipLibrary, ClipLibrary } from '../clips/ClipLibrary.js';

export class TrackManager {
  constructor() {
    this.tracks = [];
    this._listeners = new Set();
    this._sampleStorage = null; // set via setSampleStorage()
    this._customRegistry = null; // set via setCustomRegistry()
  }

  setSampleStorage(ss) { this._sampleStorage = ss; }
  setCustomRegistry(cr) { this._customRegistry = cr; }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit() {
    this._listeners.forEach((fn) => fn(this));
  }

  getTrack(id) {
    return this.tracks.find((t) => t.id === id);
  }

  async addTrack({ instrumentId, name = null, mixer = {} } = {}) {
    const entry = getInstrumentEntry(instrumentId, this._customRegistry);
    if (!entry) throw new Error(`Unknown instrument: ${instrumentId}`);

    const track = new Track({
      instrumentId,
      name: name || entry.name,
      color: entry.color,
      mixer
    });

    // Forward any per-track change (mixer edit, note added, instrument
    // swap, etc.) through the TrackManager so subscribers like the Mixer
    // and Sequencer re-render. Without this, clicking a Mute/Solo
    // button — which only fires the track's own _emit() — would not
    // trigger a re-render of the mixer strip.
    track.onChange(() => this._emit());

    this._bindInstrument(track, entry);

    // Wire audio routing: instrument output -> per-track channel
    const { channel, sendGain } = engine.createTrackChannel();
    track.channel = channel;
    track.sendGain = sendGain;
    track.instrument.output.connect(channel);

    track._applyMixer();
    this.tracks.push(track);
    this._emit();

    // For sampled instruments, wait for samples to be ready before
    // returning so callers can show a "Loading..." state if they want.
    await track.instrument.isLoaded.catch(() => {});
    return track;
  }

  /**
   * Replace a track's instrument. The audio routing (channel, sendGain) and
   * note data are preserved; only the instrument and its derived metadata
   * (name, color) change. Disposes the previous instrument's nodes and
   * reconnects the new one into the existing channel.
   */
  async setInstrument(trackId, instrumentId) {
    const track = this.getTrack(trackId);
    if (!track) return;
    const entry = getInstrumentEntry(instrumentId, this._customRegistry);
    if (!entry) return;
    if (track.instrumentId === instrumentId) return; // no-op

    // Dispose the old instrument's audio nodes. The channel/sendGain belong
    // to the track and stay.
    if (track.instrument && track.instrument.dispose) {
      try { track.instrument.dispose(); } catch { /* noop */ }
    }

    // Always update the displayed name + color to match the new instrument.
    // Preset labels like "Violin I — melody" are useful when tracks are
    // first created, but when the user reassigns an instrument via drag,
    // they expect the lane to show the new instrument's name.
    track.instrumentId = instrumentId;
    track.name = entry.name;
    track.color = entry.color;

    this._bindInstrument(track, entry);
    // Reconnect into the existing channel.
    track.instrument.output.connect(track.channel);
    track._applyMixer();
    // The track.onChange subscription set up in addTrack() forwards this
    // emit through the TrackManager, so subscribers (Mixer, Sequencer)
    // re-render with the new name/color/instrument.
    track._emit();

    await track.instrument.isLoaded.catch(() => {});
  }

  /**
   * Load an instrument and attach it to a track, including the
   * isReady -> isReady-flip bookkeeping so the UI can show a loading
   * badge. Used by both addTrack and setInstrument.
   */
  _bindInstrument(track, entry) {
    const instrument = loadInstrument(entry, this._sampleStorage);
    track.instrument = instrument;
    track.isReady = false;
    instrument.isLoaded
      .then(() => {
        if (track.instrument === instrument) {
          track.isReady = true;
          track._emit();
        }
      })
      .catch(() => {
        if (track.instrument === instrument) {
          track.isReady = true; // unblock even on error so the UI doesn't stay stuck
          track._emit();
        }
      });
  }

  removeTrack(id) {
    const idx = this.tracks.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const t = this.tracks[idx];
    t.dispose();
    this.tracks.splice(idx, 1);
    this._emit();
  }

  /**
   * Compute the effective mute state given soloing.
   * A track is muted if it's flagged muted, OR if any other track is soloed
   * and this one isn't.
   */
  effectiveMute(track) {
    if (track.mixer.muted) return true;
    const anySoloed = this.tracks.some((t) => t.mixer.soloed);
    if (anySoloed && !track.mixer.soloed) return true;
    return false;
  }

  /**
   * Schedule all tracks for the current transport play. Each track
   * decides which of its notes are audible (respecting mute/solo).
   * Returns the scheduled end time so the transport can stop on time.
   *
   * Applies engine humanization (timing/velocity jitter) to mimic a
   * live ensemble.
   */
  scheduleAll(startTime) {
    let endTime = startTime;
    for (const t of this.tracks) {
      if (this.effectiveMute(t)) continue;
      for (const n of t.notes) {
        // Skip notes that fall inside any clip instance's time range
        const inClip = t.clips.some((inst) => {
          const def = clipLibrary.getClip(inst.clipId);
          if (!def) return false;
          const clipDur = ClipLibrary.getDuration(def.notes);
          const clipStart = inst.startTime;
          const clipEnd = inst.startTime + inst.repeatCount * clipDur;
          return n.time >= clipStart && n.time < clipEnd;
        });
        if (inClip) continue;
        if (!t.instrument) continue;
        const absoluteTime = startTime + n.time;
        const tHumanized = engine.humanizeTime(absoluteTime);
        const vHumanized = engine.humanizeVelocity(n.velocity);
        t.instrument.triggerAttackRelease(
          n.pitch,
          n.duration,
          tHumanized,
          vHumanized
        );
        const end = absoluteTime + n.duration;
        if (end > endTime) endTime = end;
      }
      // Expand clip instances (after free-form note loop)
      for (const inst of t.clips) {
        const def = clipLibrary.getClip(inst.clipId);
        if (!def) continue;
        const clipDur = ClipLibrary.getDuration(def.notes);
        for (let r = 0; r < inst.repeatCount; r++) {
          const clipOffset = inst.startTime + r * clipDur;
          for (const n of def.notes) {
            if (!t.instrument) continue;
            const absoluteTime = startTime + clipOffset + n.time;
            const tHumanized = engine.humanizeTime(absoluteTime);
            const vHumanized = engine.humanizeVelocity(n.velocity);
            t.instrument.triggerAttackRelease(
              n.pitch,
              n.duration,
              tHumanized,
              vHumanized
            );
            const end = absoluteTime + n.duration;
            if (end > endTime) endTime = end;
          }
        }
      }
    }
    // Mark that a fresh schedule is in flight. The Transport uses this to
    // decide between resuming pending events vs scheduling from scratch.
    this._hasPendingSchedule = true;
    return endTime;
  }

  /**
   * True if scheduleAll() has been called since the last clearSchedule()
   * (or since construction). Used by Transport.play() to detect whether
   * a Stop followed by Play should resume the pending events or schedule
   * a fresh arrangement from the current position.
   */
  hasPendingSchedule() {
    return this._hasPendingSchedule === true;
  }

  /**
   * Mark the schedule as cleared. Called by Transport after a reset
   * (which cancels all pending events) so the next play() will build a
   * fresh schedule.
   */
  clearSchedule() {
    this._hasPendingSchedule = false;
  }

  /** Total arrangement length in seconds (max of all note ends and clip instance ends). */
  getArrangementLength() {
    let max = 0;
    for (const t of this.tracks) {
      // Free-form notes (unchanged)
      for (const n of t.notes) {
        const end = n.time + n.duration;
        if (end > max) max = end;
      }
      // Clip instances
      for (const inst of t.clips) {
        const def = clipLibrary.getClip(inst.clipId);
        if (!def) continue;
        const clipDur = ClipLibrary.getDuration(def.notes);
        const end = inst.startTime + inst.repeatCount * clipDur;
        if (end > max) max = end;
      }
    }
    return max;
  }

  clearAll() {
    for (const t of this.tracks) t.dispose();
    this.tracks = [];
    this._emit();
  }

  /**
   * Stop every live voice across all tracks. Used by Transport.stop() —
   * Tone.Transport.stop() halts the timeline, but notes already triggered
   * via Sampler.triggerAttackRelease keep ringing through their release
   * envelope. We do two things:
   *   1. Briefly lower the Sampler's release to ~10ms so releaseAll()'s
   *      fade-out is effectively instant, then restore it.
   *   2. Call releaseAll on every instrument.
   * Channel/sendGain nodes are untouched; instrument references and notes
   * are preserved.
   */
  stopAll() {
    const t = Tone.now();
    for (const tr of this.tracks) {
      if (!tr.instrument) continue;
      if (typeof tr.instrument.releaseAll === 'function') {
        // setRelease() returns the previous value, so we can shorten
        // the release for a fast fade-out and then restore it via a
        // microtask once releaseAll() has had a chance to apply it.
        const prevRelease = tr.instrument.setRelease
          ? tr.instrument.setRelease(0.01)
          : undefined;
        try {
          // The Sampler's release controls the fadeOut time of
          // releaseAll (and triggerRelease). Setting it to a tiny
          // value makes the fade nearly instant, which is what we
          // want on stop.
          tr.instrument.releaseAll(t);
        } catch { /* noop */ } finally {
          if (typeof tr.instrument.setRelease === 'function' && prevRelease !== undefined) {
            queueMicrotask(() => { tr.instrument.setRelease(prevRelease); });
          }
        }
      }
    }
  }

  /**
   * Hard-cancel every scheduled event on every instrument. Unlike
   * stopAll() (which fades voices out gracefully), this disposes the
   * underlying ToneBufferSource nodes so even future-scheduled events
   * never play. Used by Transport.reset() where the user wants a clean
   * slate with no audio leakage from a previous schedule.
   */
  cancelAllScheduled() {
    for (const tr of this.tracks) {
      if (!tr.instrument) continue;
      if (typeof tr.instrument.cancelAllScheduled === 'function') {
        try { tr.instrument.cancelAllScheduled(); } catch { /* noop */ }
      }
    }
  }

  /**
   * Pause every track. Fades out currently-active voices fast (via
   * stopAll() with shortened release) AND disposes every future-
   * scheduled sampler source, snapshotting its parameters so the next
   * resumeSchedule() call can re-trigger them. The synth path doesn't
   * need source disposal — releaseAll() is enough for in-flight voices
   * and there are no future-scheduled AudioBufferSources.
   *
   * Returns the AudioContext time at which pause happened (used by
   * the Transport to compute the time shift on resume).
   */
  pauseAll() {
    // Snapshot first! Then release. If we stopAll() first, releaseAll
    // fires onended → trackedSources cleared → pauseSchedule snapshots
    // nothing and resumeAll is a no-op.
    for (const tr of this.tracks) {
      if (!tr.instrument) continue;
      if (typeof tr.instrument.pauseSchedule === 'function') {
        try { tr.instrument.pauseSchedule(); } catch { /* noop */ }
      }
    }
    this.stopAll();
    return Tone.now();
  }

  /**
   * Resume a previously-paused schedule. `pauseTime` is the AudioContext
   * time captured when pauseAll() was called; `timeShift` is added to
   * each snapshot'd source's scheduled time so the notes land at the
   * right positions relative to the resumed Transport clock.
   *
   * If no schedule was snapshot'd (e.g. the user paused when nothing
   * was scheduled) this is a no-op.
   */
  resumeAll(pauseTime, timeShift) {
    for (const tr of this.tracks) {
      if (!tr.instrument) continue;
      if (typeof tr.instrument.resumeSchedule === 'function') {
        try { tr.instrument.resumeSchedule(timeShift); } catch { /* noop */ }
      }
    }
  }
}
